import React, { useMemo } from 'react';
import { Info, InfoCol, InfoTitle, InfoValue, LinkWrapper, NeighborhoodInsightChips, StarWrapper, StyledLink, Subtitle, Title, Wrapper } from './styled';
import { CardProps } from 'screens/Investors/HomePage/types';
import { useLocale } from 'locale';
import { FavoriteNeighbourhoodWidget } from 'screens/Investors/Neighbourhood/sections/Overview/FavoriteNeighbourhoodWidget';
import { NeighborhoodInsightChip } from '../InsightChip/NeighborhoodInsightChip';
import { NeighborhoodInsightChipConfig, NeighborhoodInsightChipType } from '../InsightChip/types';

const MAX_CHIPS = 2;

export const Card: React.FC<CardProps> = ({ mode = 'primary', info, data, cropTitle, withFavoriteStar, isCompareLink }) => {
  const { t } = useLocale();
  const isPrimary = mode === 'primary';

  const skipClick = (e: React.SyntheticEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    return false;
  };

  const neighborhoodInsights = useMemo<NeighborhoodInsightChipConfig[]>(() => {
    if (!data.neighborhoodCardLabels) return [];

    const chipsConfigs: NeighborhoodInsightChipConfig[] = [
      data.neighborhoodCardLabels.popularRoom ? {
        type: NeighborhoodInsightChipType.POPULAR_ROOM,
        text: t('investors.insights.neighborhoodCardLabels.popularRoom', { value: data.neighborhoodCardLabels.popularRoom }),
      } : null,
      data.neighborhoodCardLabels.dealsVolume ? {
        type: NeighborhoodInsightChipType.DEALS_VOLUME,
        text: t('investors.insights.neighborhoodCardLabels.dealsVolume', { value: data.neighborhoodCardLabels.dealsVolume }),
      } : null,
      data.neighborhoodCardLabels.pricePerMeter ? {
        type: NeighborhoodInsightChipType.PRICE_PER_METER,
        text: t('investors.insights.neighborhoodCardLabels.pricePerMeter', { value: data.neighborhoodCardLabels.pricePerMeter }),
      } : null,
      data.neighborhoodCardLabels.priceTrendComparedToCity ? {
        type: NeighborhoodInsightChipType.PRICE_TREND_COMPARED_TO_CITY,
        text: t('investors.insights.neighborhoodCardLabels.priceTrendComparedToCity', { value: data.neighborhoodCardLabels.priceTrendComparedToCity }),
      } : null,
    ].filter(chip => chip && chip.text).slice(0, MAX_CHIPS);

    return chipsConfigs;
  }, [ data ]);

  // TODO: skip link for first release
  return (
    <Wrapper isPrimary={isPrimary}>
      <Title title={data.neighborhoodName} cropTitle={cropTitle}>{data.neighborhoodName}</Title>
      {withFavoriteStar ? (
        <StarWrapper onClick={skipClick}>
          <FavoriteNeighbourhoodWidget docId={data.neighborhoodDocId} />
        </StarWrapper>
      ) : null}
      <Subtitle>{data.cityName}</Subtitle>
      <div style={{ height: '100%' }}>
        {neighborhoodInsights.length > 0 && (
          <NeighborhoodInsightChips data-auto="neighborhood-insight-chips">
            {neighborhoodInsights.map(insight => (
              <NeighborhoodInsightChip
                key={insight.type}
                type={insight.type}
                text={insight.text}
              />
            ))}
          </NeighborhoodInsightChips>
        )}
      </div>
      {info && isPrimary ? (
        <Info>
          <InfoCol>
            <InfoTitle>{t('investors.insights.views')}</InfoTitle>
            <InfoValue weight="medium">{info.views}</InfoValue>
          </InfoCol>
          <InfoCol>
            <InfoTitle>{t('investors.insights.apartments')}</InfoTitle>
            <InfoValue weight="medium">{info.apartments}</InfoValue>
          </InfoCol>
        </Info>
      ) : null}
      <LinkWrapper>
        <StyledLink withArrow>
          {isCompareLink ? t('investors.insights.moreCompareLink') : t('investors.insights.moreLink')}
        </StyledLink>
      </LinkWrapper>
    </Wrapper>
  );
};
